import { defineSlotRecipe } from '@chakra-ui/react';
import { Colors, TextStyles, Shadows, Space, Radii, FontWeights } from '../../types';

export const dialogSlotRecipe = defineSlotRecipe({
  slots: [
    'root',
    'backdrop',
    'positioner',
    'content',
    'title',
    'description',
    'header',
    'body',
    'footer',
    'closeTrigger',
  ],
  base: {
    header: {
      textStyle: TextStyles.H2,
      fontWeight: FontWeights.BOLD,
      color: Colors.TEXT_EMPHASIS,
      px: { base: Space.MD, sm: Space.LG },
      pt: Space.MD,
      pb: Space.SM,
      margin: 0,
    },
    // The header text renders in this slot, so stock's own lg/semibold title would beat the header's.
    title: {
      textStyle: TextStyles.H2,
      fontWeight: FontWeights.BOLD,
      margin: 0,
    },
    closeTrigger: {
      position: 'absolute',
      top: Space.SM,
      right: Space.SM,
    },
    body: {
      color: Colors.TEXT_MUTED,
      textStyle: TextStyles.P2,
      px: { base: Space.MD, sm: Space.LG },
      py: Space.SM,
      gap: Space.XS,
    },
    content: {
      bg: Colors.SURFACE_ELEVATED,
      boxShadow: Shadows.XL,
      borderRadius: Radii['2XL'],
      outline: '1px solid',
      outlineColor: Colors.BORDER_ELEVATED,
      '--dialog-z-index': 'zIndex.popover',
      zIndex: 'calc(var(--dialog-z-index) + var(--layer-index, 0))',
    },
    footer: {
      paddingStart: { sm: Space.LG },
      gap: Space.SM,
      px: Space.MD,
      pt: Space.SM,
      pb: Space.MD,
    },
    positioner: {
      display: 'flex',
      width: '100dvw',
      height: '100dvh',
      position: 'fixed',
      left: 0,
      top: 0,
      justifyContent: 'center',
      overscrollBehaviorY: 'none',
      '--dialog-z-index': 'zIndex.popover',
      zIndex: 'calc(var(--dialog-z-index) + var(--layer-index, 0))',
    },
    backdrop: {
      position: 'fixed',
      left: 0,
      top: 0,
      w: '100dvw',
      h: '100dvh',
      bg: Colors['BLACKALPHA.300'],
      _dark: { bg: Colors['BLACKALPHA.600'] },
      '--dialog-z-index': 'zIndex.popover',
      zIndex: 'calc(var(--dialog-z-index) + var(--layer-index, 0) - 1)',
    },
  },
  // `createSystem` deep-merges this into the built-in, so the structural CSS below is redundant; the size
  // scale is not, since it replaces stock's shifted scale.
  variants: {
    placement: {
      center: {
        positioner: { alignItems: 'center' },
        content: { mx: 'auto' },
      },
      top: {
        positioner: { alignItems: 'flex-start' },
        content: { mx: 'auto', my: Space.SPACE_16 },
      },
      bottom: {
        positioner: { alignItems: 'flex-end' },
        content: { mx: 'auto', my: Space.LG },
      },
    },
    scrollBehavior: {
      inside: {
        positioner: { overflow: 'hidden' },
        content: { maxH: 'calc(100% - 7.5rem)' },
        // `1px 0`: full-bleed header/footer separators, 1px on the block edges and 0 inline.
        body: { overflow: 'auto', borderWidth: '1px 0', boxShadow: Shadows.INNER },
      },
      outside: {
        positioner: { overflow: 'auto', pointerEvents: 'auto' },
      },
    },
    // Each size maps to its same-named maxW token, where stock Dialog shifts the scale up; 2xl-6xl are declared
    // so no caller's size collapses.
    size: {
      xs: { content: { maxW: 'xs' } },
      sm: { content: { maxW: 'sm' } },
      md: { content: { maxW: 'md' } },
      lg: { content: { maxW: 'lg' } },
      xl: { content: { maxW: 'xl' } },
      '2xl': { content: { maxW: '2xl' } },
      '3xl': { content: { maxW: '3xl' } },
      '4xl': { content: { maxW: '4xl' } },
      '5xl': { content: { maxW: '5xl' } },
      '6xl': { content: { maxW: '6xl' } },
      full: {
        content: { maxW: '100dvw', minH: '100dvh', borderRadius: '0' },
      },
    },
    motionPreset: {
      scale: {
        content: {
          _open: { animationName: 'scale-in, fade-in' },
          _closed: { animationName: 'scale-out, fade-out' },
        },
      },
      none: {},
    },
    // Exercised by real call sites (e.g. AddCourseModal, CurriculumPromotionsModal).
    overflow: {
      true: {
        positioner: { overflow: 'auto', alignItems: 'flex-start' },
        content: { maxH: 'none', display: 'flex', flexDirection: 'column', mx: 'auto' },
        body: { flex: '1 1 auto', minH: '150px', maxH: 'calc(100dvh - 10rem)', overflowY: 'auto' },
        header: { flexShrink: 0 },
        footer: { flexShrink: 0, flexWrap: 'wrap' },
      },
    },
  },
  // Full size is flush (`my: 0`); `placement` resolves after `size`, so only a compound beats top's margin.
  compoundVariants: [{ size: 'full', css: { content: { my: 0 } } }],
  defaultVariants: {
    size: 'md',
    scrollBehavior: 'outside',
    placement: 'top',
    motionPreset: 'scale',
  },
});
